import React, { useState, useEffect, useRef } from "react";
import DOMPurify from "dompurify";
import {
  buildInboxLocalPart,
  formatInboxEmail,
  isValidInboxUsername,
} from "../utils/inbox";

const renderClickableLinks = (text) => {
  const urlRegex =
    /(\b(https?|ftp|file):\/\/[-A-Z0-9+&@#/%?=~_|!:,.;]*[-A-Z0-9+&@#/%=~_|])/gi;
  return text.split("\n").map((line, index) => (
    <React.Fragment key={index}>
      {line.split(urlRegex).map((part, i) =>
        urlRegex.test(part) ? (
          <a
            key={i}
            href={part}
            target="_blank"
            rel="noopener noreferrer"
            className="text-emerald-400 hover:underline"
          >
            {part}
          </a>
        ) : (
          part
        ),
      )}
      <br />
    </React.Fragment>
  ));
};

function InboxPanel({
  user,
  isAdminUser,
  getAuthHeaders,
  saveActiveInbox,
  initialInboxName = "",
  autoLoad = false,
  compact = false,
}) {
  const [inboxName, setInboxName] = useState(initialInboxName);
  const [emails, setEmails] = useState([]);
  const [selectedEmailId, setSelectedEmailId] = useState(null);
  const [selectedEmail, setSelectedEmail] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [countdown, setCountdown] = useState(10);
  const [intervalId, setIntervalId] = useState(null);
  const [copyMessage, setCopyMessage] = useState(null);
  const copyMessageTimeoutRef = useRef(null);
  const autoLoadedRef = useRef(false);

  const subdomain = isAdminUser ? null : user?.inboxSubdomain;

  useEffect(() => {
    return () => {
      if (copyMessageTimeoutRef.current) clearTimeout(copyMessageTimeoutRef.current);
    };
  }, []);

  useEffect(() => {
    if (!inboxName && intervalId) {
      clearInterval(intervalId);
      setIntervalId(null);
    }
    return () => {
      if (intervalId) clearInterval(intervalId);
    };
  }, [inboxName, intervalId]);

  useEffect(() => {
    if (selectedEmailId) {
      const email = emails.find((e) => e.messageId === selectedEmailId);
      setSelectedEmail(email);
    } else {
      setSelectedEmail(null);
    }
  }, [selectedEmailId, emails]);

  useEffect(() => {
    if (initialInboxName) {
      setInboxName(initialInboxName);
    } else if (user?.activeInbox) {
      setInboxName(user.activeInbox);
    }
  }, [initialInboxName, user?.activeInbox]);

  useEffect(() => {
    if (autoLoad && initialInboxName && !autoLoadedRef.current && subdomain !== undefined) {
      autoLoadedRef.current = true;
      const timer = setTimeout(() => handleCreateInbox(false), 500);
      return () => clearTimeout(timer);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoLoad, initialInboxName, subdomain]);

  const getFullLocalPart = (name) =>
    buildInboxLocalPart(name, subdomain, isAdminUser);

  const handleCreateInbox = async (isManual = true) => {
    if (!inboxName) return;
    const username = inboxName.trim().toLowerCase();

    if (!isValidInboxUsername(username)) {
      alert("Inbox name must be 3–32 characters: letters, numbers, dots, hyphens, or underscores.");
      return;
    }

    if (!isAdminUser && !subdomain) {
      alert("Your account subdomain is still loading. Please try again in a moment.");
      return;
    }

    const localPart = getFullLocalPart(username);

    try {
      setIsLoading(true);
      await saveActiveInbox(username);
      const response = await fetch(`/inbox/${encodeURIComponent(localPart)}`, {
        headers: {
          ...getAuthHeaders(),
          "Cache-Control": "no-cache",
        },
      });
      const data = await response.json().catch(() => ({}));
      if (response.status === 401 || response.status === 403) {
        alert(data.error || "You do not have access to this inbox.");
        return;
      }
      if (!response.ok && response.status !== 304) {
        const hint =
          data.code === "database"
            ? "Database unavailable. Check MongoDB on the server."
            : data.code === "redis"
              ? "Inbox storage unavailable. Check Redis on the server."
              : data.error || `Error: ${response.status}`;
        throw new Error(hint);
      }
      const reversed = data.reverse();
      setEmails(reversed);
      if (selectedEmailId) {
        const matched = reversed.find((item) => item.messageId === selectedEmailId);
        if (!matched) setSelectedEmailId(null);
      }
      if (isManual) startAutoRefresh();
    } catch (error) {
      console.error("Fetch failed:", error);
      alert(error.message || "Failed to fetch emails");
    } finally {
      setIsLoading(false);
    }
  };

  const startAutoRefresh = () => {
    if (intervalId) clearInterval(intervalId);
    setCountdown(10);
    const id = setInterval(() => {
      setCountdown((prev) => {
        if (prev === 1) {
          handleCreateInbox(false);
          return 10;
        }
        return prev - 1;
      });
    }, 1000);
    setIntervalId(id);
  };

  const showCopyMessage = (text, isError = false) => {
    if (copyMessageTimeoutRef.current) clearTimeout(copyMessageTimeoutRef.current);
    setCopyMessage({ text, isError });
    copyMessageTimeoutRef.current = setTimeout(() => {
      setCopyMessage(null);
      copyMessageTimeoutRef.current = null;
    }, 3000);
  };

  const handleCopyEmail = () => {
    const fullEmail = formatInboxEmail(inboxName, subdomain, isAdminUser);
    navigator.clipboard
      .writeText(fullEmail)
      .then(() => showCopyMessage("Email copied to clipboard"))
      .catch(() => showCopyMessage("Failed to copy email. Please try again.", true));
  };

  const sanitizeEmailHtml = (html) =>
    DOMPurify.sanitize(html, {
      FORBID_TAGS: ["script", "iframe", "object", "embed", "form", "link", "meta", "base"],
      FORBID_ATTR: ["onerror", "onload", "onclick", "onmouseover"],
    });

  return (
    <>
      <div className="rounded-2xl border border-zinc-800 bg-zinc-900/50 p-6">
        {!isAdminUser && subdomain && (
          <p className="mb-3 text-xs text-zinc-500">
            Your private subdomain:{" "}
            <span className="font-mono text-emerald-400">.{subdomain}</span>
            {" · "}
            Emails are isolated to your account only.
          </p>
        )}
        {isAdminUser && (
          <p className="mb-3 text-xs text-zinc-500">
            Admin inbox uses{" "}
            <span className="font-mono text-emerald-400">@ghostinbox.xyz</span> without a subdomain.
          </p>
        )}

        <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-center">
          <div className="flex flex-1 flex-wrap items-center gap-2">
            <input
              type="text"
              placeholder="Enter your name"
              value={inboxName}
              onChange={(e) => setInboxName(e.target.value)}
              className="min-w-[140px] flex-1 rounded-lg border border-zinc-700 bg-zinc-950 px-4 py-2.5 text-white placeholder-zinc-500 focus:border-emerald-500 focus:outline-none focus:ring-1 focus:ring-emerald-500"
            />
            {!isAdminUser && subdomain ? (
              <span className="shrink-0 font-mono text-sm text-emerald-400/90">.{subdomain}</span>
            ) : null}
            <span className="shrink-0 font-mono text-sm text-zinc-400">@ghostinbox.xyz</span>
            {inboxName && (
              <button
                type="button"
                onClick={handleCopyEmail}
                className="rounded-lg border border-zinc-600 p-2.5 text-zinc-300 transition hover:border-zinc-400 hover:text-white"
                aria-label="Copy email address"
                title="Copy email"
              >
                <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"
                  />
                </svg>
              </button>
            )}
          </div>
          <button
            type="button"
            onClick={() => handleCreateInbox()}
            className="rounded-lg bg-emerald-500 px-6 py-2.5 text-sm font-semibold text-zinc-950 hover:bg-emerald-400"
          >
            Load Inbox
          </button>
        </div>

        {inboxName && (
          <p className="mt-2 font-mono text-xs text-zinc-500">
            Full address:{" "}
            <span className="text-zinc-300">
              {formatInboxEmail(inboxName, subdomain, isAdminUser)}
            </span>
          </p>
        )}

        {copyMessage && (
          <div
            className={`mt-3 flex items-center gap-2 rounded-lg border px-3 py-2 text-sm ${
              copyMessage.isError
                ? "border-red-800/60 bg-red-950/40 text-red-300"
                : "border-emerald-800/60 bg-emerald-950/40 text-emerald-300"
            }`}
            role="status"
          >
            {!copyMessage.isError ? (
              <svg className="h-4 w-4 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
              </svg>
            ) : null}
            {copyMessage.text}
          </div>
        )}
        <p className="mt-3 text-xs text-zinc-500">
          Auto-refresh in {countdown}s · Messages expire after 20 minutes
        </p>
      </div>

      {!compact && (
        <div className="mt-6 grid grid-cols-1 gap-4 lg:grid-cols-2 lg:gap-6 xl:gap-8">
          <div className="flex min-h-[min(42vh,28rem)] flex-col overflow-hidden rounded-xl border border-zinc-800 bg-zinc-900/40 sm:min-h-[min(48vh,32rem)] lg:min-h-[calc(100vh-13rem)] xl:min-h-[calc(100vh-11rem)]">
            <h2 className="shrink-0 border-b border-zinc-800 px-4 py-3 text-base font-semibold text-white sm:text-lg">
              Inbox
            </h2>
            <div className="flex-1 overflow-y-auto p-3 sm:p-4">
              <ul>
                {emails.map((email) => (
                  <li
                    key={email.messageId}
                    className={`cursor-pointer rounded-lg border-b border-zinc-800 py-3 px-2 transition hover:bg-zinc-800/50 ${
                      selectedEmailId === email.messageId ? "bg-zinc-800" : ""
                    }`}
                    onClick={() => setSelectedEmailId(email.messageId)}
                  >
                    <span className="text-sm text-zinc-200 sm:text-base">
                      {email.subject || "No Subject"}
                    </span>
                  </li>
                ))}
              </ul>
              {isLoading && <p className="mt-2 text-sm text-emerald-400">Loading inbox…</p>}
              {!isLoading && emails.length === 0 && (
                <p className="text-sm text-zinc-500">No emails found.</p>
              )}
            </div>
          </div>

          <div className="flex min-h-[min(42vh,28rem)] flex-col overflow-hidden rounded-xl border border-zinc-800 bg-zinc-900/40 sm:min-h-[min(48vh,32rem)] lg:min-h-[calc(100vh-13rem)] xl:min-h-[calc(100vh-11rem)]">
            <h2 className="shrink-0 border-b border-zinc-800 px-4 py-3 text-base font-semibold text-white sm:text-lg">
              Email Preview
            </h2>
            <div className="flex-1 overflow-y-auto p-3 sm:p-4">
              {selectedEmail ? (
                <div>
                  <h3 className="text-base font-bold text-white sm:text-lg">
                    {selectedEmail.subject || "No Subject"}
                  </h3>
                  <p className="mb-3 text-sm text-zinc-400">
                    From: {selectedEmail.from?.[0]?.address || "Unknown"}
                  </p>
                  {selectedEmail.html ? (
                    <div
                      className="email-content rounded-lg bg-white p-3 text-sm text-zinc-900 sm:p-4 sm:text-base"
                      dangerouslySetInnerHTML={{
                        __html: sanitizeEmailHtml(selectedEmail.html),
                      }}
                    />
                  ) : (
                    <div className="whitespace-pre-wrap text-sm text-zinc-300 sm:text-base">
                      {renderClickableLinks(selectedEmail.text || "")}
                    </div>
                  )}
                </div>
              ) : (
                <p className="text-sm text-zinc-500">Select an email to view its content.</p>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default InboxPanel;
